import { useApolloClient, useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { UPDATE_PIPELINE_INPUT_CONFIG } from '@optools/services/operator-gateway/pipeline-input-config/mutations';
import { GET_PIPELINE_INPUT_CONFIGS } from '@optools/services/operator-gateway/pipeline-input-config/queries';
import {
  PipelineInputConfigFeedTypeCode,
  PipelineInputConfigFeedTypeMapping,
} from '@optools/services/operator-gateway/pipeline-input-config/types';
import {
  PipelineInputConfigInfo,
  Region,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  PipelineInputConfigForm,
  PipelineInputConfigFormValues,
} from './pipeline-input-config-form';

export interface PipelineInputConfigDialogProps {
  config: PipelineInputConfigInfo;
}

export function PipelineInputConfigDialog({ config }: PipelineInputConfigDialogProps) {
  const apolloClient = useApolloClient();
  const [updatePipelineInputConfigMutation, { loading }] = useMutation(
    UPDATE_PIPELINE_INPUT_CONFIG,
  );

  const [open, setOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (loading) return;
    setOpen(open);
  };

  const handleSubmit = (values: PipelineInputConfigFormValues) => {
    const { feedTypeCode, region, venueId } = config;

    updatePipelineInputConfigMutation({
      onCompleted: ({ updatePipelineInputConfig }) => {
        if (updatePipelineInputConfig) {
          toast.success('Pipeline Input Config successfully updated');

          apolloClient.cache.updateQuery(
            {
              query: GET_PIPELINE_INPUT_CONFIGS,
              variables: { region: config.region ?? Region.EAST, venueId: config.venueId ?? 0 },
            },
            (query) => {
              if (!query) return { pipelineInputConfigs: [] };

              const configIndex = query.pipelineInputConfigs.findIndex(
                (item) => item.id === config.id && item.region === config.region,
              );

              const pipelineInputConfigs = [...query.pipelineInputConfigs];

              pipelineInputConfigs.splice(configIndex, 1, {
                ...values,
                feedTypeCode,
                id: config.id,
                region: config.region,
                venueId,
              });

              return { pipelineInputConfigs };
            },
          );
          setOpen(false);
        } else {
          toast.error('Failed to update pipeline input config', { duration: Infinity });
        }
      },
      variables: {
        feedType:
          PipelineInputConfigFeedTypeMapping[feedTypeCode as PipelineInputConfigFeedTypeCode],
        pipelineInputConfig: values,
        region,
        venueId: venueId ?? 0,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">Edit</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Config</DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <span>Feed Type Code: {config.feedTypeCode}</span>
            <span>Config ID: {config.id}</span>
            {config.region && <span>Region: {config.region}</span>}
          </DialogDescription>
        </DialogHeader>

        <PipelineInputConfigForm config={config} onSubmit={handleSubmit}>
          <DialogFooter className="mt-8">
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button disabled={loading} loading={loading}>
              Save
            </Button>
          </DialogFooter>
        </PipelineInputConfigForm>
      </DialogContent>
    </Dialog>
  );
}
